import { useState } from 'react'
import { Check, RotateCcw, Settings2 } from 'lucide-react'
import { useLabels } from '@/lib/terminology'
import { useUpdateTerminology } from '@/hooks/useTerminology'
import { DEFAULT_LABELS, TERMINOLOGY_OPTIONS, type AppLabels } from '@/types'
import { useAuth } from '@/lib/auth'

type CustomizableKey = keyof Omit<AppLabels, 'pipeline'>

interface TermFieldProps {
  label: string
  fieldKey: CustomizableKey
  value: string
  options: string[]
  onChange: (key: CustomizableKey, value: string) => void
  disabled?: boolean
}

function TermField({ label, fieldKey, value, options, onChange, disabled }: TermFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = value === option
          return (
            <button
              key={option}
              type="button"
              disabled={disabled}
              onClick={() => onChange(fieldKey, option)}
              className={`
                px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                ${isSelected
                  ? 'bg-primary-500 text-white'
                  : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {option}
              {isSelected && <Check className="w-3 h-3 inline-block ml-1.5" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function TerminologySettings() {
  const labels = useLabels()
  const { membership } = useAuth()
  const updateTerminology = useUpdateTerminology()

  const isEditable = membership?.role === 'owner' || membership?.role === 'admin'

  // Local state for unsaved changes
  const [draft, setDraft] = useState<Record<CustomizableKey, string>>({
    collections: labels.collections,
    recordings: labels.recordings,
    guests: labels.guests,
    schedule: labels.schedule,
    appearances: labels.appearances,
  })

  const [hasChanges, setHasChanges] = useState(false)

  const handleChange = (key: CustomizableKey, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }

  const handleSave = () => {
    updateTerminology.mutate({
      term_collections: draft.collections === DEFAULT_LABELS.collections ? null : draft.collections,
      term_recordings: draft.recordings === DEFAULT_LABELS.recordings ? null : draft.recordings,
      term_guests: draft.guests === DEFAULT_LABELS.guests ? null : draft.guests,
      term_schedule: draft.schedule === DEFAULT_LABELS.schedule ? null : draft.schedule,
      term_appearances: draft.appearances === DEFAULT_LABELS.appearances ? null : draft.appearances,
    }, {
      onSuccess: () => {
        setHasChanges(false)
      },
    })
  }

  const handleReset = () => {
    setDraft({
      collections: DEFAULT_LABELS.collections,
      recordings: DEFAULT_LABELS.recordings,
      guests: DEFAULT_LABELS.guests,
      schedule: DEFAULT_LABELS.schedule,
      appearances: DEFAULT_LABELS.appearances,
    })
    setHasChanges(true)
  }

  const fields: { label: string; key: CustomizableKey }[] = [
    { label: 'Collections / Podcasts', key: 'collections' },
    { label: 'Recordings / Episodes', key: 'recordings' },
    { label: 'Guests / Speakers', key: 'guests' },
    { label: 'Schedule / Calendar', key: 'schedule' },
    { label: 'Appearances / Bookings', key: 'appearances' },
  ]

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Terminology
          </h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
            Customize how things are labeled throughout the app
          </p>
        </div>
        <Settings2 className="w-5 h-5 text-zinc-400" />
      </div>

      {/* Pipeline is always "Flow" */}
      <div className="mb-4 px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Pipeline</span>
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            Always &ldquo;{DEFAULT_LABELS.pipeline}&rdquo;
          </span>
        </div>
      </div>

      <div className="space-y-5">
        {fields.map(({ label, key }) => (
          <TermField
            key={key}
            label={label}
            fieldKey={key}
            value={draft[key]}
            options={TERMINOLOGY_OPTIONS[key]}
            onChange={handleChange}
            disabled={!isEditable || updateTerminology.isPending}
          />
        ))}
      </div>

      {!isEditable && (
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-4">
          Only team owners and admins can change terminology settings.
        </p>
      )}

      {isEditable && (
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-zinc-200 dark:border-zinc-800">
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateTerminology.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateTerminology.isPending ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={handleReset}
            disabled={updateTerminology.isPending}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Defaults
          </button>

          {updateTerminology.isError && (
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Failed to save. Please try again.
            </p>
          )}
          {updateTerminology.isSuccess && !hasChanges && (
            <p className="text-sm text-green-600 dark:text-green-400">
              Saved!
            </p>
          )}
        </div>
      )}
    </div>
  )
}
